import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Advisor, CurriculumModule, Topic, TopicStatus, User, TopicContentType, CurriculumLevel, EvaluationQuestion, RolePlaySession, ChatMessage, BrandingSettings, EvaluationResult, CertificateSettings, Signatory, LibraryCategory, LibraryResource, TrainingLesson } from '../types';
import { api } from '../services/api';
import { LessonBuilderModule } from './LessonBuilder';
import Header from '../components/Header';
import ProgressBar from '../components/ProgressBar';
import { Spinner, UserGroupIcon, ChevronRightIcon, CheckCircleIcon, PlayIcon, LockIcon, PlusIcon, PencilIcon, TrashIcon, UploadIcon, DownloadIcon, RestoreIcon, BookCheckIcon, BookOpenIcon, RobotIcon, CogIcon, DocumentTextIcon, FolderIcon, QuestionMarkCircleIcon, PresentationChartBarIcon } from '../components/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const MAX_FILE_SIZE_MB = 90;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const sameId = (a: string | number, b: string | number) => String(a) === String(b);
const getAllowedMimeTypes = (contentType: TopicContentType) => {
    if (contentType === 'video') return 'video/*';
    if (contentType === 'presentation') return '.pdf,.ppt,.pptx,.key,.odp,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
    return '.pdf,application/pdf';
};

// Helper function to remove accents for jsPDF compatibility with standard fonts
const normalizeText = (text: string): string => {
    if (!text) return '';
    // This will remove accents and other diacritical marks.
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const calculateProgress = (progress: Advisor['progress'], assignedIds: string[]): number => {
  if (assignedIds.length === 0) return 100;
  const completedCount = assignedIds.filter(id => progress[id] === TopicStatus.COMPLETED || progress[String(id)] === TopicStatus.COMPLETED).length;
  return Math.round((completedCount / assignedIds.length) * 100);
};

const AdvisorDetails: React.FC<{ advisor: Advisor; levels: CurriculumLevel[]; modules: CurriculumModule[], topics: Topic[] }> = ({ advisor, levels, modules, topics }) => {
    
    const getStatusIcon = (status: TopicStatus) => {
        switch (status) {
            case TopicStatus.COMPLETED:
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            case TopicStatus.IN_PROGRESS:
                return <PlayIcon className="h-5 w-5 text-yellow-500" />;
            default:
                return <LockIcon className="h-5 w-5 text-gray-400" />;
        }
    };
    
    const getTopicStatus = (topic: Topic): TopicStatus => {
        return advisor.progress[topic.id] as TopicStatus || advisor.progress[String(topic.id)] as TopicStatus || TopicStatus.LOCKED;
    };

    const topicsByModule = useMemo(() => {
        const assignedModuleIds = new Set((advisor.assignedModuleIds || []).map(String));
        return modules
            .filter(module => assignedModuleIds.has(String(module.id)))
            .map(module => ({
                ...module,
                topics: topics.filter(t => sameId(t.moduleId, module.id))
            }));
    }, [modules, topics, advisor.assignedModuleIds]);

    const handleDownloadPdf = () => {
        const doc = new jsPDF();
        doc.setFontSize(20);
        doc.text("Informe de Progreso del Asesor", 14, 22);
        doc.setFontSize(12);
        doc.text(`Asesor: ${advisor.name}`, 14, 32);
        doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 44);
        
        const tableData = topicsByModule.flatMap(module => 
            module.topics.map(topic => [
                levels.find(l => sameId(l.id, module.levelId))?.title || 'N/A',
                module.title,
                topic.title,
                (getTopicStatus(topic) || 'Pendiente').replace(/_/g, ' ')
            ])
        );

        autoTable(doc, {
            startY: 50,
            head: [['Nivel', 'Módulo', 'Tema', 'Estado']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [41, 128, 185] },
        });

        doc.save(`progreso_${advisor.name.replace(/\s/g, '_')}.pdf`);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Progreso Detallado</h3>
                <button
                    onClick={handleDownloadPdf}
                    className="flex items-center space-x-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
                >
                    <DownloadIcon className="h-4 w-4" />
                    <span>Descargar PDF</span>
                </button>
            </div>
            {topicsByModule.map(module => (
                <div key={module.id} className="bg-slate-700 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-200 mb-3 flex items-center justify-between">
                        <span>{module.title}</span>
                        <span className="text-xs text-slate-300">{module.topics.filter(t => getTopicStatus(t) === TopicStatus.COMPLETED).length}/{module.topics.length} temas</span>
                    </h4>
                    <ul className="space-y-2">
                        {module.topics.map(topic => (
                            <li key={topic.id} className="flex items-center justify-between p-2 bg-slate-800 rounded">
                                <span className="text-gray-300">{topic.title}</span>
                                {getStatusIcon(getTopicStatus(topic))}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' }> = ({ isOpen, onClose, title, children, size = 'lg' }) => {
    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl', '2xl': 'max-w-2xl'
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4" onClick={onClose}>
            <div className={`bg-slate-800 rounded-xl shadow-lg w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col border border-slate-700`} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-4 border-b border-slate-700">
                    <h3 className="text-xl font-semibold text-white">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
                </div>
                <div className="p-6 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};

const ConfirmDeleteModal: React.FC<{ isOpen: boolean; onClose: () => void; onConfirm: () => void; title: string; message: string; }> = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
            <div className="text-white">
                <p className="mb-6">{message}</p>
                <div className="flex justify-end space-x-4">
                    <button onClick={onClose} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded">Cancelar</button>
                    <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">Confirmar</button>
                </div>
            </div>
        </Modal>
    );
};

// Form Modal Component
const CurriculumFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    type: 'level' | 'module' | 'topic' | 'question';
    mode: 'add' | 'edit';
    initialData?: any;
    topics?: Topic[];
}> = ({ isOpen, onClose, onSubmit, type, mode, initialData, topics = [] }) => {
    const [formData, setFormData] = useState<any>({});
    const [publishedLessons, setPublishedLessons] = useState<TrainingLesson[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const data = { ...(initialData || {}) }; // Start with a copy of initialData or an empty object

        // Apply default values for 'add' mode if they are not already present
        if (mode === 'add') {
            if (type === 'level' || type === 'module') {
                if (data.displayOrder === undefined) {
                    data.displayOrder = 0;
                }
            }
            if (type === 'topic') {
                if (!data.dependencies) {
                    data.dependencies = [];
                }
            }
            if (type === 'question') {
                if (!data.options) {
                    data.options = ['', '', '', ''];
                }
                if (data.correctAnswer === undefined) {
                    data.correctAnswer = 0;
                }
            }
        }

        // For edit mode, just ensure complex fields are initialized if they happen to be missing from the data
        if (mode === 'edit') {
            if (type === 'topic' && !data.dependencies) {
                data.dependencies = [];
            }
            if (type === 'question' && !data.options) {
                data.options = ['', '', '', ''];
            }
        }
        
        if (type === 'topic' && String(data.contentUrl || '').startsWith('lesson-builder:')) {
            data.contentType = 'lesson_builder';
        }
        setFormData(data);

    }, [initialData, type, mode]);

    useEffect(() => {
        if (isOpen && type === 'topic') {
            api.getTrainingLessons('published').then(lessons => setPublishedLessons(lessons.filter(lesson => lesson.usage_status !== 'in_use' || `lesson-builder:${lesson.id}` === formData.contentUrl))).catch(() => setPublishedLessons([]));
        }
    }, [isOpen, type, formData.contentUrl]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.currentTarget;
        setFormData((prev: any) => ({ ...(prev || {}), [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            const contentType = (formData?.contentType || 'pdf') as TopicContentType;
            const ext = file.name.toLowerCase().split('.').pop() || '';
            if (contentType === 'presentation' && (ext === 'ppt' || ext === 'pptx')) {
                alert('Para evitar errores 504 y mejorar rendimiento, usa liga embebible (Google Slides/Drive) o exporta la presentación a PDF antes de subirla.');
                e.currentTarget.value = '';
                return;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                alert(`El archivo es demasiado grande. El límite actual es de ${MAX_FILE_SIZE_MB} MB. Para archivos más grandes, se debe ajustar la configuración del servidor.`);
                e.currentTarget.value = ''; // Clear the file input
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData((prev: any) => ({
                    ...(prev || {}),
                    contentUrl: event.target?.result as string,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleTopicDependencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedIds = Array.from(e.currentTarget.selectedOptions, option => option.value);
        setFormData((prev: any) => ({...(prev || {}), dependencies: selectedIds }));
    };

    const handleQuestionOptionChange = (index: number, value: string) => {
        const newOptions = [...(formData?.options || ['', '', '', ''])];
        newOptions[index] = value;
        setFormData((prev: any) => ({ ...(prev || {}), options: newOptions }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const title = `${mode === 'add' ? 'Añadir' : 'Editar'} ${type}`;
    
    const renderTopicFormFields = () => (
        <>
            <input name="title" value={formData.title || ''} onChange={handleChange} placeholder="Título del Tema" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
            <textarea name="description" value={formData.description || ''} onChange={handleChange} placeholder="Descripción" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
            <select name="contentType" value={formData.contentType || 'pdf'} onChange={handleChange} className="w-full bg-slate-700 p-2 rounded border border-slate-600">
                <option value="pdf">PDF</option>
                <option value="video">Video</option>
                <option value="presentation">Presentación</option>
                <option value="lesson_builder">Lección interactiva</option>
            </select>
            
            {formData.contentType === 'lesson_builder' ? (
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-300">Lección publicada del Constructor</label>
                    <select name="contentUrl" value={formData.contentUrl || ''} onChange={handleChange} className="w-full bg-slate-700 p-2 rounded border border-slate-600">
                        <option value="">Selecciona una lección disponible</option>
                        {publishedLessons.map(lesson => <option key={lesson.id} value={`lesson-builder:${lesson.id}`}>{lesson.title} · {lesson.slides_count} slides</option>)}
                    </select>
                    <p className="mt-1 text-xs text-slate-400">Las lecciones En Uso no aparecen para evitar duplicados.</p>
                </div>
            ) : (
            <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">URL del Contenido o Subir Archivo</label>
                <div className="flex items-center space-x-2">
                    <input
                        name="contentUrl"
                        value={formData.contentUrl || ''}
                        onChange={handleChange}
                        placeholder="Pega URL (YouTube/Drive/Slides/PDF) o sube archivo"
                        className="w-full bg-slate-700 p-2 rounded border border-slate-600"
                    />
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept={getAllowedMimeTypes((formData.contentType || 'pdf') as TopicContentType)}
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded"
                        title="Subir archivo"
                    >
                        <UploadIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
            )}

             <label className="text-sm text-gray-400">Dependencias (Ctrl+Click para múltiple)</label>
            <select
                multiple
                name="dependencies"
                value={formData.dependencies || []}
                onChange={handleTopicDependencyChange}
                className="w-full h-32 bg-slate-700 p-2 rounded border border-slate-600"
            >
                {topics.filter(t => t.id !== formData.id).map(topic => (
                    <option key={topic.id} value={topic.id}>{topic.title}</option>
                ))}
            </select>
        </>
    );
    
    const renderQuestionFormFields = () => (
         <>
            <textarea name="question" value={formData.question || ''} onChange={handleChange} placeholder="Texto de la pregunta" required rows={3} className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
            {(formData.options || []).map((opt: string, index: number) => (
                 <div key={index} className="flex items-center space-x-2">
                    {/* FIX: Explicitly typed the event object to resolve currentTarget.value error. */}
                    <input type="radio" name="correctAnswer" value={index} checked={Number(formData.correctAnswer) === index} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({...formData, correctAnswer: Number(e.currentTarget.value)})} className="form-radio text-blue-500 bg-slate-600"/>
                    <input type="text" value={opt} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuestionOptionChange(index, e.currentTarget.value)} placeholder={`Opción ${index + 1}`} required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                </div>
            ))}
        </>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <form onSubmit={handleSubmit} className="space-y-4 text-white">
                {(type === 'level' || type === 'module') && (
                    <>
                        <input name="title" value={formData.title || ''} onChange={handleChange} placeholder="Título" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                        <textarea name="description" value={formData.description || ''} onChange={handleChange} placeholder="Descripción" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                        <input type="number" name="displayOrder" value={formData.displayOrder || 0} onChange={handleChange} placeholder="Orden" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                    </>
                )}
                {type === 'topic' && renderTopicFormFields()}
                {type === 'question' && renderQuestionFormFields()}
                <div className="flex justify-end pt-4">
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Guardar</button>
                </div>
            </form>
        </Modal>
    );
};


const QuestionManagerModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    module: CurriculumModule | null;
    questions: EvaluationQuestion[];
    onDataChange: () => void;
}> = ({ isOpen, onClose, module, questions, onDataChange }) => {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<EvaluationQuestion | null>(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState<EvaluationQuestion | null>(null);

    if (!module) return null;

    const moduleQuestions = questions.filter(q => q.moduleId === module.id);

    const openFormModal = (question?: EvaluationQuestion) => {
        setEditingQuestion(question || null);
        setIsFormModalOpen(true);
    };

    const handleFormSubmit = async (data: any) => {
        try {
            if (editingQuestion) {
                await api.updateQuestion({ ...editingQuestion, ...data });
            } else {
                await api.addQuestion({ ...data, moduleId: module.id });
            }
            onDataChange();
        } catch (error) {
            console.error("Failed to save question:", error);
            alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error."}`);
        } finally {
            setIsFormModalOpen(false);
        }
    };

    const moveTopic = async (moduleId: string, topicId: string, direction: -1 | 1) => {
        const ordered = topics
            .filter(t => sameId(t.moduleId, moduleId))
            .sort((a, b) => Number(a.displayOrder ?? a.id) - Number(b.displayOrder ?? b.id));
        const currentIndex = ordered.findIndex(t => sameId(t.id, topicId));
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) return;
        const next = [...ordered];
        [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
        await api.reorderTopics(moduleId, next.map(t => t.id));
        onDataChange();
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;
        try {
            await api.deleteQuestion(deleteConfirmation.id);
            onDataChange();
        } catch (error) {
            console.error("Failed to delete question:", error);
            alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error."}`);
        } finally {
            setDeleteConfirmation(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Gestionar Preguntas: ${module.title}`} size="2xl">
            <div className="space-y-4">
                <div className="text-right">
                    <button onClick={() => openFormModal()} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center space-x-2 ml-auto">
                        <PlusIcon className="h-5 w-5" />
                        <span>Añadir Pregunta</span>
                    </button>
                </div>
                
                {moduleQuestions.length > 0 ? (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                        {moduleQuestions.map(q => (
                             <div key={q.id} className="flex justify-between items-center bg-slate-700 p-3 rounded">
                                <p className="text-sm text-gray-300 flex-1">{q.question}</p>
                                <div className="flex space-x-2 flex-shrink-0 ml-4">
                                    <button onClick={() => openFormModal(q)} className="p-1 text-gray-400 hover:text-white"><PencilIcon className="h-4 w-4" /></button>
                                    <button onClick={() => setDeleteConfirmation(q)} className="p-1 text-gray-400 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-gray-500 py-8">No hay preguntas para este módulo.</p>
                )}
            </div>

            {isFormModalOpen && (
                <CurriculumFormModal
                    isOpen={isFormModalOpen}
                    onClose={() => setIsFormModalOpen(false)}
                    onSubmit={handleFormSubmit}
                    type="question"
                    mode={editingQuestion ? 'edit' : 'add'}
                    initialData={editingQuestion}
                />
            )}
             {deleteConfirmation && (
                <ConfirmDeleteModal
                    isOpen={!!deleteConfirmation}
                    onClose={() => setDeleteConfirmation(null)}
                    onConfirm={handleDelete}
                    title="Confirmar Eliminación"
                    message={`¿Estás seguro de que quieres eliminar la pregunta: "${deleteConfirmation.question}"?`}
                />
            )}
        </Modal>
    );
};

// Curriculum Management Tab
const CurriculumManager: React.FC<{
    levels: CurriculumLevel[];
    modules: CurriculumModule[];
    topics: Topic[];
    questions: EvaluationQuestion[];
    onDataChange: () => void;
}> = ({ levels, modules, topics, questions, onDataChange }) => {

    const [modalState, setModalState] = useState<{ type: 'level' | 'module' | 'topic' | 'question', mode: 'add' | 'edit', data?: any, parentId?: string }>({ type: 'level', mode: 'add' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: string, id: string, title: string } | null>(null);
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
    const [currentModuleForQuestions, setCurrentModuleForQuestions] = useState<CurriculumModule | null>(null);

    const openModal = (type: 'level' | 'module' | 'topic', mode: 'add' | 'edit', data?: any, parentId?: string) => {
        setModalState({ type, mode, data, parentId } as any);
        setIsModalOpen(true);
    };

    const openQuestionModal = (module: CurriculumModule) => {
        setCurrentModuleForQuestions(module);
        setIsQuestionModalOpen(true);
    };

    const handleFormSubmit = async (data: any) => {
        try {
            switch (modalState.type) {
                case 'level':
                    modalState.mode === 'add' ? await api.addCurriculumLevel(data) : await api.updateCurriculumLevel(data);
                    break;
                case 'module':
                    modalState.mode === 'add' ? await api.addCurriculumModule({ ...data, levelId: modalState.parentId }) : await api.updateCurriculumModule(data);
                    break;
                case 'topic':
                    { const topicPayload = { ...data, contentType: data.contentType === 'lesson_builder' ? 'presentation' : data.contentType }; modalState.mode === 'add' ? await api.addTopic({ ...topicPayload, moduleId: modalState.parentId }) : await api.updateTopic(topicPayload); }
                    break;
                // Question form submission is now handled by QuestionManagerModal
            }
            onDataChange();
        } catch (error) {
            console.error("Failed to save curriculum data:", error);
            alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
        } finally {
            setIsModalOpen(false);
        }
    };

    const moveTopic = async (moduleId: string, topicId: string, direction: -1 | 1) => {
        const ordered = topics
            .filter(t => sameId(t.moduleId, moduleId))
            .sort((a, b) => Number(a.displayOrder ?? a.id) - Number(b.displayOrder ?? b.id));
        const currentIndex = ordered.findIndex(t => sameId(t.id, topicId));
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) return;
        const next = [...ordered];
        [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
        await api.reorderTopics(moduleId, next.map(t => t.id));
        onDataChange();
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;
        try {
            switch (deleteConfirmation.type) {
                case 'level': await api.deleteCurriculumLevel(deleteConfirmation.id); break;
                case 'module': await api.deleteCurriculumModule(deleteConfirmation.id); break;
                case 'topic': await api.deleteTopic(deleteConfirmation.id); break;
                case 'question': await api.deleteQuestion(deleteConfirmation.id); break;
            }
            onDataChange();
        } catch (error) {
            console.error(`Failed to delete ${deleteConfirmation.type}:`, error);
             alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
        } finally {
            setDeleteConfirmation(null);
        }
    };

    const sortedLevels = useMemo(() => [...levels].sort((a, b) => a.displayOrder - b.displayOrder), [levels]);

    return (
        <div className="space-y-4">
            <div className="text-right">
                <button onClick={() => openModal('level', 'add')} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center space-x-2 ml-auto">
                    <PlusIcon className="h-5 w-5" />
                    <span>Añadir Nivel</span>
                </button>
            </div>
            {sortedLevels.map(level => {
                const levelModules = modules.filter(m => m.levelId === level.id).sort((a,b)=> a.displayOrder - b.displayOrder);
                return (
                    <div key={level.id} className="bg-slate-700 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-xl font-bold text-white">{level.title}</h3>
                            <div className="flex space-x-2">
                                <button onClick={() => openModal('level', 'edit', level)} className="p-2 text-gray-400 hover:text-white"><PencilIcon className="h-5 w-5" /></button>
                                <button onClick={() => setDeleteConfirmation({ type: 'level', id: level.id, title: level.title })} className="p-2 text-gray-400 hover:text-red-500"><TrashIcon className="h-5 w-5" /></button>
                                <button onClick={() => openModal('module', 'add', undefined, level.id)} className="p-2 text-gray-400 hover:text-green-500"><PlusIcon className="h-5 w-5" /></button>
                            </div>
                        </div>
                        <p className="text-gray-400 text-sm mb-4">{level.description}</p>
                        {levelModules.map(module => {
                             const moduleTopics = topics.filter(t => t.moduleId === module.id).sort((a, b) => Number(a.displayOrder ?? a.id) - Number(b.displayOrder ?? b.id));
                            return (
                            <div key={module.id} className="bg-slate-800 p-3 rounded-lg ml-4 mb-3">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-lg font-semibold text-gray-200">{module.title}</h4>
                                    <div className="flex space-x-2">
                                        <button onClick={() => openModal('module', 'edit', module)} className="p-1 text-gray-400 hover:text-white"><PencilIcon className="h-4 w-4" /></button>
                                        <button onClick={() => setDeleteConfirmation({ type: 'module', id: module.id, title: module.title })} className="p-1 text-gray-400 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
                                        <button onClick={() => openModal('topic', 'add', undefined, module.id)} title="Añadir Tema" className="p-1 text-gray-400 hover:text-green-500"><BookOpenIcon className="h-4 w-4" /></button>
                                        <button onClick={() => openQuestionModal(module)} title="Gestionar Preguntas" className="p-1 text-gray-400 hover:text-blue-500"><QuestionMarkCircleIcon className="h-4 w-4" /></button>
                                    </div>
                                </div>
                                 <p className="text-gray-400 text-xs mb-3">{module.description}</p>
                                <div className="space-y-2">
                                    {moduleTopics.map((topic, topicIndex) => (
                                        <div key={topic.id} className="flex justify-between items-center bg-slate-700/50 p-2 rounded">
                                            <p className="text-sm text-gray-300">{topic.title}</p>
                                            <div className="flex space-x-2">
                                                <button disabled={topicIndex === 0} onClick={() => moveTopic(module.id, topic.id, -1)} title="Subir tema" className="p-1 text-gray-400 hover:text-white disabled:opacity-30">↑</button>
                                                <button disabled={topicIndex === moduleTopics.length - 1} onClick={() => moveTopic(module.id, topic.id, 1)} title="Bajar tema" className="p-1 text-gray-400 hover:text-white disabled:opacity-30">↓</button>
                                                <button onClick={() => openModal('topic', 'edit', topic)} className="p-1 text-gray-400 hover:text-white"><PencilIcon className="h-4 w-4" /></button>
                                                <button onClick={() => setDeleteConfirmation({ type: 'topic', id: topic.id, title: topic.title })} className="p-1 text-gray-400 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )})}
                    </div>
                );
            })}
             {isModalOpen && (
                <CurriculumFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={handleFormSubmit}
                    type={modalState.type}
                    mode={modalState.mode}
                    initialData={modalState.data}
                    topics={topics} // For dependency selection
                />
            )}
             {isQuestionModalOpen && (
                <QuestionManagerModal
                    isOpen={isQuestionModalOpen}
                    onClose={() => setIsQuestionModalOpen(false)}
                    module={currentModuleForQuestions}
                    questions={questions}
                    onDataChange={onDataChange}
                />
            )}
            {deleteConfirmation && (
                <ConfirmDeleteModal
                    isOpen={!!deleteConfirmation}
                    onClose={() => setDeleteConfirmation(null)}
                    onConfirm={handleDelete}
                    title={`Confirmar Eliminación de ${deleteConfirmation.type}`}
                    message={`¿Estás seguro de que quieres eliminar "${deleteConfirmation.title}"? Esta acción no se puede deshacer y eliminará todo el contenido asociado (módulos, temas, progreso del asesor, etc.).`}
                />
            )}
        </div>
    );
};

// Advisor Management Tab
const AdvisorManager: React.FC<{
    advisors: Advisor[];
    deletedAdvisors: Advisor[];
    topics: Topic[];
    modules: CurriculumModule[];
    levels: CurriculumLevel[];
    onDataChange: () => void;
}> = ({ advisors, deletedAdvisors, topics, modules, levels, onDataChange }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAdvisor, setEditingAdvisor] = useState<Advisor | null>(null);
    const [selectedAdvisor, setSelectedAdvisor] = useState<Advisor | null>(null);
    const openModal = (advisor?: Advisor) => {
        setEditingAdvisor(advisor || null);
        setIsModalOpen(true);
    };
    
    const handleFormSubmit = async (data: any) => {
        try {
            if (editingAdvisor) {
                await api.updateAdvisor({ ...editingAdvisor, ...data });
            } else {
                await api.addAdvisor(data);
            }
            onDataChange();
        } catch (error) {
            console.error("Failed to save advisor data:", error);
            alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
        } finally {
            setIsModalOpen(false);
        }
    };
    
    const handleDelete = async (advisor: Advisor) => {
        if(window.confirm(`¿Estás seguro que quieres eliminar a ${advisor.name}? Esta acción moverá al asesor a la papelera.`)){
            try {
                await api.updateAdvisor({ ...advisor, isDeleted: true, isActive: false });
                onDataChange();
            } catch (error) {
                console.error("Failed to delete advisor:", error);
                alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
            }
        }
    };

    const handleRestore = async (advisor: Advisor) => {
        if(window.confirm(`¿Estás seguro que quieres restaurar a ${advisor.name}?`)){
            try {
                await api.updateAdvisor({ ...advisor, isDeleted: false });
                onDataChange();
            } catch (error) {
                console.error("Failed to restore advisor:", error);
                alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                 <h2 className="text-2xl font-bold text-white">Asesores Activos</h2>
                <button onClick={() => openModal()} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                    <PlusIcon className="h-5 w-5" />
                    <span>Añadir Asesor</span>
                </button>
            </div>
            <div className="overflow-x-auto bg-slate-800 rounded-lg shadow-lg">
                <table className="min-w-full text-left text-sm text-gray-300">
                    <thead className="border-b border-slate-700 text-xs uppercase">
                        <tr>
                            <th className="px-4 py-3">Nombre</th>
                            <th className="px-4 py-3">Correo</th>
                            <th className="px-4 py-3">Rol</th>
                            <th className="px-4 py-3">Progreso</th>
                            <th className="px-4 py-3">Estado</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {advisors.map(advisor => (
                            <tr key={advisor.id} className="border-b border-slate-700 last:border-0 hover:bg-slate-700/50">
                                <td className="px-4 py-3 font-medium text-white cursor-pointer hover:text-blue-400" onClick={() => setSelectedAdvisor(advisor)}>{advisor.name}</td>
                                <td className="px-4 py-3">{advisor.email}</td>
                                <td className="px-4 py-3">{advisor.apexRole || advisor.role}</td>
                                <td className="px-4 py-3"><ProgressBar value={calculateProgress(advisor.progress, advisor.assignedTopics)} /></td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${advisor.isActive ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                                        {advisor.isActive ? 'Activo' : 'Inactivo'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                     <button onClick={() => openModal(advisor)} className="p-2 text-gray-400 hover:text-white"><PencilIcon className="h-5 w-5" /></button>
                                     <button onClick={() => handleDelete(advisor)} className="p-2 text-gray-400 hover:text-red-500"><TrashIcon className="h-5 w-5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {deletedAdvisors.length > 0 && (
                 <div className="space-y-4 pt-8">
                    <h2 className="text-2xl font-bold text-white">Asesores en Papelera</h2>
                    <div className="overflow-x-auto bg-slate-800 rounded-lg shadow-lg">
                       <table className="min-w-full text-left text-sm text-gray-300">
                            <thead className="border-b border-slate-700 text-xs uppercase">
                                <tr>
                                    <th className="px-4 py-3">Nombre</th>
                                    <th className="px-4 py-3">Correo</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {deletedAdvisors.map(advisor => (
                                    <tr key={advisor.id} className="border-b border-slate-700 last:border-0 hover:bg-slate-700/50">
                                        <td className="px-4 py-3 font-medium text-white">{advisor.name}</td>
                                        <td className="px-4 py-3">{advisor.email}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => handleRestore(advisor)} className="p-2 text-gray-400 hover:text-green-500" title="Restaurar Asesor"><RestoreIcon className="h-5 w-5" /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {selectedAdvisor && (
                <Modal isOpen={!!selectedAdvisor} onClose={() => setSelectedAdvisor(null)} title={`Detalle de ${selectedAdvisor.name}`} size="2xl">
                    <AdvisorDetails advisor={selectedAdvisor} levels={levels} modules={modules} topics={topics} />
                </Modal>
            )}
            {isModalOpen && (
                <AdvisorFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={handleFormSubmit}
                    advisor={editingAdvisor}
                    topics={topics}
                    modules={modules}
                    advisors={advisors}
                />
            )}
        </div>
    );
};

const AdvisorFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    advisor: Advisor | null;
    topics: Topic[];
    modules: CurriculumModule[];
    advisors: Advisor[];
}> = ({ isOpen, onClose, onSubmit, advisor, topics, modules, advisors }) => {
    const [formData, setFormData] = useState({
        name: advisor?.name || '',
        email: advisor?.email || '',
        password: '',
        role: advisor?.apexRole || advisor?.role || 'Asesor',
        managerId: advisor?.managerId || '',
        assignedTopics: advisor?.assignedTopics || [],
        assignedModuleIds: advisor?.assignedModuleIds || [],
        isActive: advisor?.isActive === undefined ? true : advisor.isActive,
    });


    useEffect(() => {
        setFormData({
            name: advisor?.name || '',
            email: advisor?.email || '',
            password: '',
            role: advisor?.apexRole || advisor?.role || 'Asesor',
            managerId: advisor?.managerId || '',
            assignedTopics: (advisor?.assignedTopics || []).map(String),
            assignedModuleIds: (advisor?.assignedModuleIds || []).map(String),
            isActive: advisor?.isActive === undefined ? true : advisor.isActive,
        });
    }, [advisor, isOpen]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type, checked } = e.currentTarget;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };
    
    const updateAssignmentsFromModules = (selectedIds: string[]) => {
        const topicIdsFromModules = topics
            .filter(topic => selectedIds.some(moduleId => sameId(topic.moduleId, moduleId)))
            .map(topic => String(topic.id));

        setFormData(prev => ({
            ...prev,
            assignedModuleIds: selectedIds,
            assignedTopics: Array.from(new Set(topicIdsFromModules)),
        }));
    };
    
    const handleModuleChecklistChange = (moduleId: string, checked: boolean) => {
        const currentSet = new Set((formData.assignedModuleIds || []).map(String));
        if (checked) {
            currentSet.add(String(moduleId));
        } else {
            currentSet.delete(String(moduleId));
        }
        updateAssignmentsFromModules(Array.from(currentSet));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const dataToSubmit: any = { ...formData };
        if (!dataToSubmit.password) {
            delete dataToSubmit.password;
        }
        onSubmit(dataToSubmit);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={advisor ? 'Editar Asesor' : 'Añadir Asesor'}>
            <form onSubmit={handleSubmit} className="space-y-4 text-white">
                <input name="name" value={formData.name} onChange={handleChange} placeholder="Nombre Completo" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Correo Electrónico" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder={advisor ? 'Nueva contraseña (opcional)' : 'Contraseña'} required={!advisor} className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                <select name="role" value={formData.role} onChange={handleChange} className="w-full bg-slate-700 p-2 rounded border border-slate-600">
                    <option value="Asesor">Asesor</option>
                    <option value="Gerente">Gerente</option>
                    <option value="Reclutador">Reclutador</option>
                    <option value="Asistente">Staff / Asistente</option>
                    <option value="Presentador">Presentador</option>
                    <option value="Promotor">Promotor</option>
                </select>
                <select name="managerId" value={formData.managerId || ''} onChange={handleChange} className="w-full bg-slate-700 p-2 rounded border border-slate-600">
                    <option value="">Sin gerente asignado</option>
                    {advisors
                        .filter(a => (a.apexRole || a.role) === 'Gerente' && String(a.userId || a.id) !== String(advisor?.userId || advisor?.id || ''))
                        .map(a => <option key={a.userId || a.id} value={a.userId || a.id}>{a.name}</option>)}
                </select>
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-300">Cursos Asignados</label>
                    <div className="max-h-52 overflow-y-auto bg-slate-700 p-2 rounded border border-slate-600 space-y-2">
                        {modules.map(module => {
                            const checked = formData.assignedModuleIds.some(id => sameId(id, module.id));
                            return (
                                <label key={module.id} className="flex items-start gap-3 p-2 bg-slate-800 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => handleModuleChecklistChange(String(module.id), e.currentTarget.checked)}
                                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-200">{module.title}</span>
                                </label>
                            );
                        })}
                        {modules.length === 0 && (
                            <p className="text-sm text-gray-400">Primero crea cursos/módulos en la sección de Currículo.</p>
                        )}
                    </div>
                </div>
                 <div className="flex items-center">
                    <input type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"/>
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-300">Activo</label>
                </div>
                <div className="flex justify-end pt-4">
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Guardar</button>
                </div>
            </form>
        </Modal>
    );
};

const SettingsTab: React.FC<{ branding: BrandingSettings | null, onBrandingUpdate: (settings: Omit<BrandingSettings, 'key'>) => void }> = ({ branding, onBrandingUpdate }) => {
    const [settings, setSettings] = useState({
        logoUrl: branding?.logoUrl || '',
        primaryColor: branding?.primaryColor || '#2563eb',
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'logoUrl') => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setSettings(prev => ({ ...prev, [field]: event.target?.result as string }));
            };
            reader.readAsDataURL(file);
        }
    };
    
    const handleSaveChanges = () => {
        onBrandingUpdate(settings);
        alert("Configuración guardada. La página se recargará para aplicar los cambios.");
        window.location.reload();
    };
    
    const handleGenerateFeaturePDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text(normalizeText("Inventario de Funcionalidades - APEX University"), 14, 22);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(normalizeText("Este documento detalla todos los módulos y funcionalidades de la aplicación."), 14, 32);

        const adminFeatures = [
            {
                module: "Gestión de Asesores",
                description: "Permite listar, crear, editar y eliminar (mover a papelera) asesores. Se puede activar/desactivar cuentas, asignar temas específicos del currículo y visualizar el progreso general de cada uno."
            },
            {
                module: "Gestión de Contenido",
                description: "Centro de control para el currículo. Permite crear, editar y eliminar Niveles, Módulos, Temas y Preguntas de Evaluación. Soporta dependencias entre temas y varios tipos de contenido (PDF, video)."
            },
            {
                module: "Biblioteca",
                description: "Repositorio de archivos. El administrador puede crear categorías y subir recursos (PDFs, imágenes, etc.) para que los asesores los descarguen como material de apoyo."
            },
            {
                module: "Historial de Prácticas",
                description: "Visualización centralizada de todas las sesiones de Role Play realizadas por todos los asesores. Permite revisar transcripciones y el feedback proporcionado por la IA a cada asesor."
            },
            {
                module: "Marca y Estilo",
                description: "Personalización de la apariencia de la aplicación. Permite cambiar el logo y el color primario para alinear la plataforma con la identidad visual de la empresa."
            },
            {
                module: "Certificado",
                description: "Configuración del certificado de finalización. Permite editar el texto, logo, colores y añadir firmantes (nombre, cargo y firma) que aparecerán en el PDF final del asesor."
            }
        ];

        const advisorFeatures = [
            {
                module: "Ruta de Aprendizaje",
                description: "Dashboard principal del asesor. Muestra los niveles y módulos asignados. Los temas se desbloquean progresivamente según las dependencias y la finalización de módulos y evaluaciones."
            },
            {
                module: "Visor de Contenido",
                description: "Modal interactivo que muestra el contenido de un tema (PDF, video, presentación). El asesor debe marcar el tema como 'Completado' para poder avanzar."
            },
            {
                module: "Arena de Práctica (Role Play)",
                description: "Simulador de conversaciones con un cliente IA. El asesor configura un escenario y perfil de cliente, y practica sus habilidades. La sesión es transcrita y analizada por la IA para dar feedback."
            },
            {
                module: "Biblioteca de Recursos",
                description: "Acceso a un modal donde se listan todos los recursos subidos por el administrador, organizados por categoría. Permite la descarga directa de los archivos."
            },
            {
                module: "Evaluaciones",
                description: "Página dedicada para realizar las evaluaciones de cada módulo y la evaluación final. Presenta preguntas de opción múltiple y muestra el resultado (puntuación, aprobado/reprobado) al finalizar."
            },
            {
                module: "Descarga de Certificado",
                description: "Una vez aprobada la evaluación final, el asesor puede generar y descargar su certificado de finalización en formato PDF, personalizado con su nombre y los datos configurados por el administrador."
            }
        ];

        autoTable(doc, {
            startY: 40,
            head: [['Módulo/Sección', 'Descripción de la Funcionalidad']],
            body: adminFeatures.map(f => [normalizeText(f.module), normalizeText(f.description)]),
            didDrawPage: (data) => {
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(normalizeText("Panel de Administrador"), data.settings.margin.left, 38);
            },
            headStyles: { fillColor: [22, 163, 74] },
            margin: { top: 45 }
        });

        autoTable(doc, {
            head: [['Módulo/Sección', 'Descripción de la Funcionalidad']],
            body: advisorFeatures.map(f => [normalizeText(f.module), normalizeText(f.description)]),
            didDrawPage: (data) => {
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(normalizeText("Portal del Asesor"), data.settings.margin.left, data.cursor?.y ? data.cursor.y - 2 : 10);
            },
            headStyles: { fillColor: [37, 99, 235] },
        });

        doc.save('Inventario_Funcionalidades_APEX_University.pdf');
    };

    return (
        <div className="space-y-6 max-w-2xl">
            <h2 className="text-2xl font-bold text-white">Configuración de Marca</h2>
             <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-300">Logo de la Empresa (URL o Subir)</label>
                    <div className="flex items-center space-x-4">
                        {/* FIX: Explicitly typed the event object to resolve currentTarget.value error. */}
                        <input
                            type="text"
                            value={settings.logoUrl}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(p => ({ ...p, logoUrl: e.currentTarget.value }))}
                            placeholder="https://example.com/logo.png"
                            className="w-full bg-slate-700 p-2 rounded border border-slate-600"
                        />
                         <input type="file" id="logo-upload" className="hidden" onChange={e => handleFileChange(e, 'logoUrl')} accept="image/*" />
                         <label htmlFor="logo-upload" className="cursor-pointer bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded">Subir</label>
                    </div>
                    {settings.logoUrl && <img src={settings.logoUrl} alt="Logo Preview" className="mt-4 h-16 bg-white p-2 rounded-lg" />}
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-300">Color Primario</label>
                    <div className="flex items-center space-x-2">
                        <input
                            type="color"
                            value={settings.primaryColor}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(p => ({ ...p, primaryColor: e.currentTarget.value }))}
                            className="p-1 h-10 w-10 block bg-slate-700 border border-slate-600 cursor-pointer rounded-lg"
                        />
                        <input
                            type="text"
                            value={settings.primaryColor}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings(p => ({ ...p, primaryColor: e.currentTarget.value }))}
                            className="w-full bg-slate-700 p-2 rounded border border-slate-600"
                        />
                    </div>
                </div>
                 <div className="pt-4 flex justify-end">
                    <button onClick={handleSaveChanges} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                        Guardar Cambios y Recargar
                    </button>
                </div>
            </div>
             <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 space-y-4">
                 <h3 className="text-xl font-bold text-white">Documentación y Revisión</h3>
                 <p className="text-gray-400">Genera un documento PDF con el listado completo de funcionalidades de la aplicación para facilitar la revisión y las pruebas.</p>
                 <div className="pt-2 flex justify-end">
                    <button onClick={handleGenerateFeaturePDF} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center space-x-2">
                        <DownloadIcon className="h-5 w-5" />
                        <span>Descargar Inventario de Funcionalidades (PDF)</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

const CertificateSettingsTab: React.FC = () => {
    const [settings, setSettings] = useState<Omit<CertificateSettings, 'key'>>({
        text: 'Por la presente se certifica que [ADVISOR_NAME] ha completado con éxito el Boot Camp APEX el [COMPLETION_DATE].',
        logoUrl: '',
        primaryColor: '#0f172a',
        secondaryColor: '#4338ca',
        signatories: []
    });
    
    useEffect(() => {
        api.getCertificateSettings().then(data => {
            if (data) {
                setSettings(data);
            }
        });
    }, []);

    const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.currentTarget;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'logoUrl' | `sig-${number}`) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const dataUrl = event.target?.result as string;
                if (field === 'logoUrl') {
                    setSettings(prev => ({ ...prev, logoUrl: dataUrl }));
                } else {
                    const index = parseInt(field.split('-')[1]);
                    const newSignatories = [...settings.signatories];
                    newSignatories[index].signatureUrl = dataUrl;
                    setSettings(prev => ({ ...prev, signatories: newSignatories }));
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSignatoryChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        const newSignatories = [...settings.signatories];
        newSignatories[index] = { ...newSignatories[index], [name]: value };
        setSettings(prev => ({ ...prev, signatories: newSignatories }));
    };

    const addSignatory = () => {
        setSettings(prev => ({
            ...prev,
            signatories: [...prev.signatories, { id: `sig${Date.now()}`, name: '', title: '', signatureUrl: '' }]
        }));
    };

    const removeSignatory = (index: number) => {
        setSettings(prev => ({
            ...prev,
            signatories: prev.signatories.filter((_, i) => i !== index)
        }));
    };

    const handleSaveChanges = async () => {
        try {
            await api.saveCertificateSettings(settings);
            alert("Configuración del certificado guardada.");
        } catch (e) {
            console.error(e);
            alert("Error al guardar la configuración.");
        }
    };
    
    const handlePreviewCertificate = () => {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Background
        doc.setFillColor(settings.primaryColor || '#0f172a');
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        doc.setFillColor(settings.secondaryColor || '#4338ca');
        doc.rect(0, 0, pageWidth, 20, 'F');
        doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
        
        // Logo
        if (settings.logoUrl && settings.logoUrl.startsWith('data:image')) {
           try {
              doc.addImage(settings.logoUrl, undefined, pageWidth / 2 - 15, 25, 30, 30);
           } catch(e) {
               console.error("Error adding logo to certificate preview:", e);
           }
        }

        // Main Text
        const defaultCertText = 'Por la presente se certifica que [ADVISOR_NAME] ha completado con éxito el Boot Camp APEX el [COMPLETION_DATE].';
        const certificateText = settings.text || defaultCertText;
        const mainText = certificateText
            .replace(/\[ADVISOR_NAME\]/g, 'NOMBRE DEL ASESOR DE PRUEBA')
            .replace(/\[COMPLETION_DATE\]/g, new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }));

        doc.setFontSize(28);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#FFFFFF');
        doc.text(normalizeText("CERTIFICADO DE FINALIZACIÓN"), pageWidth / 2, 70, { align: 'center' });
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        doc.text(normalizeText(mainText), pageWidth / 2, 90, { align: 'center', maxWidth: 220 });
        
        // Signatories
        if (settings.signatories && settings.signatories.length > 0) {
            const signatoryCount = settings.signatories.length;
            const totalWidth = signatoryCount * 80;
            const startX = (pageWidth - totalWidth) / 2 + 10;
            
            settings.signatories.forEach((sig, index) => {
                const x = startX + index * 80;
                if(sig.signatureUrl && sig.signatureUrl.startsWith('data:image')) {
                    try {
                        doc.addImage(sig.signatureUrl, undefined, x, pageHeight - 65, 60, 20);
                    } catch(e) {
                        console.error(`Error adding signature for ${sig.name} to preview:`, e);
                    }
                }
                doc.setLineWidth(0.5);
                doc.line(x, pageHeight - 40, x + 60, pageHeight - 40);
                doc.setFontSize(10);
                doc.text(normalizeText(sig.name || ''), x + 30, pageHeight - 35, { align: 'center' });
                doc.setFontSize(8);
                doc.text(normalizeText(sig.title || ''), x + 30, pageHeight - 30, { align: 'center' });
            });
        }
        
        doc.output('dataurlnewwindow');
    };

    return (
        <div className="space-y-6 max-w-3xl">
            <h2 className="text-2xl font-bold text-white">Configuración de Certificado</h2>
             <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-300">Texto del Certificado</label>
                    <textarea name="text" value={settings.text} onChange={handleSettingChange} rows={4} className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                    <p className="text-xs text-gray-400 mt-1">Usa [ADVISOR_NAME] y [COMPLETION_DATE] como placeholders.</p>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1 text-gray-300">Logo del Certificado (Subir)</label>
                     <input type="file" id="cert-logo-upload" className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" onChange={e => handleFileChange(e, 'logoUrl')} accept="image/*" />
                    {settings.logoUrl && <img src={settings.logoUrl} alt="Logo Preview" className="mt-4 h-16 bg-white p-2 rounded-lg" />}
                </div>
                 <div className="flex items-center space-x-4">
                     <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">Color Primario (Fondo)</label>
                        <input type="color" name="primaryColor" value={settings.primaryColor} onChange={handleSettingChange} className="p-1 h-10 w-10 block bg-slate-700 border border-slate-600 cursor-pointer rounded-lg" />
                     </div>
                     <div>
                        <label className="block text-sm font-medium mb-1 text-gray-300">Color Secundario (Acentos)</label>
                        <input type="color" name="secondaryColor" value={settings.secondaryColor} onChange={handleSettingChange} className="p-1 h-10 w-10 block bg-slate-700 border border-slate-600 cursor-pointer rounded-lg" />
                     </div>
                 </div>

                <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Firmantes</h3>
                    <div className="space-y-4">
                        {settings.signatories.map((sig, index) => (
                            <div key={sig.id} className="flex items-center space-x-2 p-3 bg-slate-700 rounded-lg">
                                <div className="flex-1 space-y-2">
                                    <input type="text" name="name" value={sig.name} onChange={e => handleSignatoryChange(index, e)} placeholder="Nombre del Firmante" className="w-full bg-slate-600 p-2 rounded border border-slate-500"/>
                                    <input type="text" name="title" value={sig.title} onChange={e => handleSignatoryChange(index, e)} placeholder="Cargo" className="w-full bg-slate-600 p-2 rounded border border-slate-500"/>
                                     <input type="file" id={`sig-upload-${index}`} className="block w-full text-sm text-gray-400 file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-500 file:text-white hover:file:bg-slate-400" onChange={e => handleFileChange(e, `sig-${index}`)} accept="image/*" />
                                </div>
                                {sig.signatureUrl && <img src={sig.signatureUrl} alt="Signature Preview" className="h-10 bg-white p-1 rounded" />}
                                <button onClick={() => removeSignatory(index)} className="p-2 text-gray-400 hover:text-red-500"><TrashIcon className="h-5 w-5"/></button>
                            </div>
                        ))}
                    </div>
                    <button onClick={addSignatory} className="mt-4 flex items-center space-x-2 text-sm text-blue-400 hover:text-blue-300">
                        <PlusIcon className="h-4 w-4" />
                        <span>Añadir Firmante</span>
                    </button>
                </div>

                 <div className="pt-4 flex justify-between items-center">
                    <button onClick={handlePreviewCertificate} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded">
                        Previsualizar Certificado
                    </button>
                    <button onClick={handleSaveChanges} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                        Guardar Configuración
                    </button>
                </div>
            </div>
        </div>
    );
};

const LibraryFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    type: 'category' | 'resource';
    mode: 'add' | 'edit';
    initialData?: any;
}> = ({ isOpen, onClose, onSubmit, type, mode, initialData }) => {
    const [formData, setFormData] = useState<any>(initialData || {});
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setFormData(initialData || {});
    }, [initialData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.currentTarget;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                alert(`El archivo es demasiado grande. El límite actual es de ${MAX_FILE_SIZE_MB} MB. Para archivos más grandes, se debe ajustar la configuración del servidor.`);
                e.currentTarget.value = ''; // Clear the file input
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData((prev: any) => ({
                    ...prev,
                    fileData: event.target?.result as string,
                    fileName: file.name,
                    fileType: file.type,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const title = `${mode === 'add' ? 'Añadir' : 'Editar'} ${type === 'category' ? 'Categoría' : 'Recurso'}`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <form onSubmit={handleSubmit} className="space-y-4 text-white">
                {type === 'category' ? (
                    <input name="name" value={formData.name || ''} onChange={handleChange} placeholder="Nombre de la categoría" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                ) : (
                    <>
                        <input name="title" value={formData.title || ''} onChange={handleChange} placeholder="Título del recurso" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                        <textarea name="description" value={formData.description || ''} onChange={handleChange} placeholder="Descripción" required className="w-full bg-slate-700 p-2 rounded border border-slate-600" />
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-300">Archivo</label>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} required={mode === 'add'} className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            {formData.fileName && <p className="text-xs text-gray-400 mt-1">Archivo actual: {formData.fileName}</p>}
                        </div>
                    </>
                )}
                <div className="flex justify-end pt-4">
                    <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">Guardar</button>
                </div>
            </form>
        </Modal>
    );
};

// Library Management Tab
const LibraryManager: React.FC<{
    categories: LibraryCategory[];
    resources: LibraryResource[];
    onDataChange: () => void;
}> = ({ categories, resources, onDataChange }) => {
    const [modalState, setModalState] = useState<{ type: 'category' | 'resource', mode: 'add' | 'edit', data?: any, parentId?: string }>({ type: 'category', mode: 'add' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: string, id: string, title: string } | null>(null);

    const openModal = (type: 'category' | 'resource', mode: 'add' | 'edit', data?: any, parentId?: string) => {
        setModalState({ type, mode, data, parentId });
        setIsModalOpen(true);
    };

    const handleFormSubmit = async (data: any) => {
        try {
            switch (modalState.type) {
                case 'category':
                    modalState.mode === 'add' ? await api.addLibraryCategory(data) : await api.updateLibraryCategory(data);
                    break;
                case 'resource':
                    modalState.mode === 'add' ? await api.addLibraryResource({ ...data, categoryId: modalState.parentId }) : await api.updateLibraryResource(data);
                    break;
            }
            onDataChange();
        } catch (error) {
            console.error(`Failed to save library data:`, error);
            alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
        } finally {
            setIsModalOpen(false);
        }
    };

    const moveTopic = async (moduleId: string, topicId: string, direction: -1 | 1) => {
        const ordered = topics
            .filter(t => sameId(t.moduleId, moduleId))
            .sort((a, b) => Number(a.displayOrder ?? a.id) - Number(b.displayOrder ?? b.id));
        const currentIndex = ordered.findIndex(t => sameId(t.id, topicId));
        const targetIndex = currentIndex + direction;
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) return;
        const next = [...ordered];
        [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
        await api.reorderTopics(moduleId, next.map(t => t.id));
        onDataChange();
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;
        try {
            switch (deleteConfirmation.type) {
                case 'category':
                    await api.deleteLibraryCategory(deleteConfirmation.id);
                    break;
                case 'resource':
                    await api.deleteLibraryResource(deleteConfirmation.id);
                    break;
            }
            onDataChange();
        } catch (error) {
            console.error(`Failed to delete ${deleteConfirmation.type}:`, error);
            alert(`Error: ${error instanceof Error ? error.message : "Ocurrió un error inesperado."}`);
        } finally {
            setDeleteConfirmation(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="text-right">
                <button onClick={() => openModal('category', 'add')} className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center space-x-2 ml-auto">
                    <PlusIcon className="h-5 w-5" />
                    <span>Añadir Categoría</span>
                </button>
            </div>
            {categories.map(category => (
                <div key={category.id} className="bg-slate-700 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-xl font-bold text-white">{category.name}</h3>
                        <div className="flex space-x-2">
                            <button onClick={() => openModal('category', 'edit', category)} className="p-2 text-gray-400 hover:text-white"><PencilIcon className="h-5 w-5" /></button>
                            <button onClick={() => setDeleteConfirmation({ type: 'category', id: category.id, title: category.name })} className="p-2 text-gray-400 hover:text-red-500"><TrashIcon className="h-5 w-5" /></button>
                            <button onClick={() => openModal('resource', 'add', undefined, category.id)} title="Añadir Recurso" className="p-2 text-gray-400 hover:text-green-500"><PlusIcon className="h-5 w-5" /></button>
                        </div>
                    </div>
                    <div className="space-y-2 mt-4">
                        {resources.filter(r => r.categoryId === category.id).map(resource => (
                            <div key={resource.id} className="flex justify-between items-center bg-slate-800 p-3 rounded">
                                <div>
                                    <p className="font-semibold text-gray-300">{resource.title}</p>
                                    <p className="text-xs text-gray-400">{resource.description}</p>
                                </div>
                                <div className="flex space-x-2 flex-shrink-0 ml-4">
                                    <button onClick={() => openModal('resource', 'edit', resource)} className="p-1 text-gray-400 hover:text-white"><PencilIcon className="h-4 w-4" /></button>
                                    <button onClick={() => setDeleteConfirmation({ type: 'resource', id: resource.id, title: resource.title })} className="p-1 text-gray-400 hover:text-red-500"><TrashIcon className="h-4 w-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            {isModalOpen && (
                <LibraryFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSubmit={handleFormSubmit}
                    type={modalState.type}
                    mode={modalState.mode}
                    initialData={modalState.data}
                />
            )}
            {deleteConfirmation && (
                <ConfirmDeleteModal
                    isOpen={!!deleteConfirmation}
                    onClose={() => setDeleteConfirmation(null)}
                    onConfirm={handleDelete}
                    title={`Confirmar Eliminación de ${deleteConfirmation.type}`}
                    message={`¿Estás seguro de que quieres eliminar "${deleteConfirmation.title}"?`}
                />
            )}
        </div>
    );
};

const RolePlayHistory: React.FC<{ sessions: RolePlaySession[], advisors: Advisor[] }> = ({ sessions, advisors }) => {
    const [selectedSession, setSelectedSession] = useState<RolePlaySession | null>(null);

    const getAdvisorName = (advisorId: string) => {
        return advisors.find(a => a.id === advisorId)?.name || 'Desconocido';
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Historial de Prácticas de Role Play</h2>
            <div className="overflow-x-auto bg-slate-800 rounded-lg shadow-lg">
                <table className="min-w-full text-left text-sm text-gray-300">
                     <thead className="border-b border-slate-700 text-xs uppercase">
                        <tr>
                            <th className="px-4 py-3">Asesor</th>
                            <th className="px-4 py-3">Fecha</th>
                            <th className="px-4 py-3">Escenario</th>
                            <th className="px-4 py-3">Puntuación</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sessions.sort((a,b) => b.date - a.date).map(session => (
                            <tr key={session.id} className="border-b border-slate-700 last:border-0 hover:bg-slate-700/50">
                                <td className="px-4 py-3 font-medium text-white">{getAdvisorName(session.advisorId)}</td>
                                <td className="px-4 py-3">{new Date(session.date).toLocaleString()}</td>
                                <td className="px-4 py-3">{session.scenarioTitle}</td>
                                <td className="px-4 py-3 font-bold text-blue-400">{session.feedback.overallScore}%</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => setSelectedSession(session)} className="text-sm font-semibold text-blue-400 hover:text-blue-300">Ver Detalles</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
             <Modal isOpen={!!selectedSession} onClose={() => setSelectedSession(null)} title="Detalle de Sesión de Role Play" size="2xl">
                {selectedSession && (
                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-white">
                        <div className="bg-slate-700 p-4 rounded-lg">
                            <h4 className="font-bold mb-2 text-gray-200">Transcripción</h4>
                            <div className="h-96 overflow-y-auto space-y-3 p-2 border rounded bg-slate-800 border-slate-600">
                                {selectedSession.transcript.map((msg, index) => (
                                    <div key={index} className={`flex items-start gap-2 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-xs px-3 py-2 rounded-lg ${msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-600 text-gray-200'}`}>
                                            <p className="text-sm">{msg.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-slate-700 p-4 rounded-lg">
                            <h4 className="font-bold mb-2 text-gray-200">Feedback del Coach de IA</h4>
                            <div className="space-y-4">
                                <div className="text-center p-4 bg-slate-800 rounded-lg border border-slate-600">
                                    <p className="text-sm text-gray-400">Puntuación</p>
                                    <p className="text-4xl font-bold text-blue-400">{selectedSession.feedback.overallScore}%</p>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-green-400">Puntos Fuertes</h5>
                                    <ul className="list-disc list-inside text-sm text-gray-300 mt-1 pl-2">{selectedSession.feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-yellow-400">Áreas de Mejora</h5>
                                    <ul className="list-disc list-inside text-sm text-gray-300 mt-1 pl-2">{selectedSession.feedback.areasForImprovement.map((a, i) => <li key={i}>{a}</li>)}</ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
           </Modal>
        </div>
    );
};


type AdminTab = 'advisors' | 'curriculum' | 'roleplay' | 'library' | 'lessonBuilder' | 'settings';

export const AdminDashboard: React.FC<{ user: User, onLogout: () => void, branding: BrandingSettings | null, onBrandingUpdate: (settings: Omit<BrandingSettings, 'key'>) => void }> = ({ user, onLogout, branding, onBrandingUpdate }) => {
    const [activeTab, setActiveTab] = useState<AdminTab>('advisors');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Data states
    const [advisors, setAdvisors] = useState<Advisor[]>([]);
    const [deletedAdvisors, setDeletedAdvisors] = useState<Advisor[]>([]);
    const [levels, setLevels] = useState<CurriculumLevel[]>([]);
    const [modules, setModules] = useState<CurriculumModule[]>([]);
    const [topics, setTopics] = useState<Topic[]>([]);
    const [questions, setQuestions] = useState<EvaluationQuestion[]>([]);
    const [sessions, setSessions] = useState<RolePlaySession[]>([]);
    const [categories, setCategories] = useState<LibraryCategory[]>([]);
    const [resources, setResources] = useState<LibraryResource[]>([]);
    const canUseLessonBuilder = ['SuperAdmin', 'Promotor', 'ADMIN'].includes(String(user.apexRole || user.role));

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [
                advisorsData,
                deletedAdvisorsData,
                curriculumData,
                questionsData,
                sessionsData,
                categoriesData,
                resourcesData
            ] = await Promise.all([
                api.getAdvisors(),
                api.getDeletedAdvisors(),
                api.getFullCurriculum(),
                api.getAllQuestions(),
                api.getAllRolePlaySessions(),
                api.getLibraryCategories(),
                api.getLibraryResources()
            ]);
            setAdvisors(advisorsData);
            setDeletedAdvisors(deletedAdvisorsData);
            setLevels(curriculumData.levels);
            setModules(curriculumData.modules);
            setTopics(curriculumData.topics);
            setQuestions(questionsData);
            setSessions(sessionsData);
            setCategories(categoriesData);
            setResources(resourcesData);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch dashboard data.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const renderContent = () => {
        if (loading) return <div className="flex justify-center items-center p-10"><Spinner className="h-10 w-10 text-white" /></div>;
        if (error) return <div className="text-red-500 p-4 bg-red-900/20 border border-red-800 rounded-lg">{error}</div>;

        switch(activeTab) {
            case 'advisors':
                return <AdvisorManager advisors={advisors.filter(a => !a.isDeleted)} deletedAdvisors={deletedAdvisors.concat(advisors.filter(a => a.isDeleted))} topics={topics} modules={modules} levels={levels} onDataChange={fetchData} />;
            case 'curriculum':
                return <CurriculumManager levels={levels} modules={modules} topics={topics} questions={questions} onDataChange={fetchData} />;
             case 'roleplay':
                return <RolePlayHistory sessions={sessions} advisors={advisors} />;
            case 'library':
                return <LibraryManager categories={categories} resources={resources} onDataChange={fetchData} />;
            case 'lessonBuilder':
                return canUseLessonBuilder ? <LessonBuilderModule /> : <div className="rounded-lg border border-red-800 bg-red-950/30 p-4 text-red-200">Módulo disponible únicamente para SuperAdmin y Promotor.</div>;
            case 'settings':
                return (
                    <div className="space-y-8">
                        <SettingsTab branding={branding} onBrandingUpdate={onBrandingUpdate} />
                        <CertificateSettingsTab />
                    </div>
                );
            default:
                return null;
        }
    };
    
    const TabButton: React.FC<{ tabId: AdminTab; title: string; icon: React.ReactNode }> = ({ tabId, title, icon }) => (
        <button
            onClick={() => setActiveTab(tabId)}
            className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors duration-200 ${activeTab === tabId ? 'bg-blue-600 text-white font-semibold' : 'text-gray-300 hover:bg-slate-700'}`}
        >
            {icon}
            <span>{title}</span>
        </button>
    );

    return (
        <div className="min-h-screen bg-slate-900">
            <Header user={user} onLogout={onLogout} logoUrl={branding?.logoUrl} />
            <div className="container mx-auto p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                           <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible">
                                <TabButton tabId="advisors" title="Asesores" icon={<UserGroupIcon className="h-5 w-5"/>} />
                                <TabButton tabId="curriculum" title="Contenido" icon={<BookOpenIcon className="h-5 w-5"/>} />
                                <TabButton tabId="roleplay" title="Prácticas" icon={<RobotIcon className="h-5 w-5"/>} />
                                <TabButton tabId="library" title="Biblioteca" icon={<FolderIcon className="h-5 w-5"/>} />
                                {canUseLessonBuilder && <TabButton tabId="lessonBuilder" title="Constructor de Lecciones" icon={<PresentationChartBarIcon className="h-5 w-5"/>} />}
                                <TabButton tabId="settings" title="Configuración" icon={<CogIcon className="h-5 w-5"/>} />
                           </nav>
                        </div>
                    </aside>
                    <main className="flex-1 bg-slate-800 p-4 sm:p-6 rounded-xl border border-slate-700 min-w-0">
                        {renderContent()}
                    </main>
                </div>
            </div>
        </div>
    );
};
